import { useCallback, useEffect, useState } from 'react';
import { apiUrl, assetUrl } from '../config/api';
import { SKILL_OPTIONS } from '../constants/registration';
import { apiFetch, getSessionStatus } from '../utils/session';
import './HelpRequest.css';
import './Community.css';

const PHOTO_LIMIT = 3;

export default function Talent() {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [skills, setSkills] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [files, setFiles] = useState([]);
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const approved = getSessionStatus() === 'Active';

  const applyShowcase = (showcase) => {
    setTitle(showcase?.title || '');
    setDescription(showcase?.description || '');
    setSkills(showcase?.skills || []);
    setPhotos(showcase?.photos || []);
    setHidden(Boolean(showcase?.hidden));
    setFiles([]);
  };

  const load = useCallback(async () => {
    setError('');
    const res = await apiFetch(apiUrl('/talent'));
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not load your showcase');
    applyShowcase(data.showcase);
  }, []);

  useEffect(() => {
    if (!approved) {
      setLoading(false);
      return;
    }
    load()
      .catch((e) => setError(e.message || 'Could not load your showcase'))
      .finally(() => setLoading(false));
  }, [approved, load]);

  const toggleSkill = (skill) => {
    setSkills((current) =>
      current.includes(skill) ? current.filter((item) => item !== skill) : [...current, skill]
    );
  };

  const onPickFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    const room = PHOTO_LIMIT - photos.length - files.length;
    if (room <= 0) {
      setError('A showcase can have only three photos');
      event.target.value = '';
      return;
    }
    setError('');
    setFiles((current) => [...current, ...picked.slice(0, room)]);
    event.target.value = '';
  };

  const save = async (e) => {
    e.preventDefault();
    setBusyKey('save');
    setError('');
    setNotice('');
    try {
      const res = await apiFetch(apiUrl('/talent'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, description, skills, photos, hidden }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not save your showcase');
        return;
      }
      let latest = data.showcase;
      for (const file of files) {
        const body = new FormData();
        body.append('file', file);
        const uploaded = await apiFetch(apiUrl('/talent/photos'), { method: 'POST', body });
        const uploadedData = await uploaded.json().catch(() => ({}));
        if (!uploaded.ok) {
          applyShowcase(latest);
          setError(uploadedData.error || 'Could not add a photo');
          return;
        }
        latest = uploadedData.showcase;
      }
      applyShowcase(latest);
      setNotice(latest?.hidden ? 'Showcase saved and hidden from My Community.' : 'Showcase saved for your area.');
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusyKey('');
    }
  };

  const setVisibility = async (nextHidden) => {
    setBusyKey('hide');
    setError('');
    setNotice('');
    try {
      const res = await apiFetch(apiUrl('/talent'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden: nextHidden }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not update your showcase');
        return;
      }
      applyShowcase(data.showcase);
      setNotice(nextHidden ? 'Hidden from My Community. Your profile stays as it is.' : 'Visible again in your area.');
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div className="help-page">
      <div className="help-shell">
        <header className="help-header">
          <div>
            <h1 className="help-title">My talent</h1>
            <p className="help-sub">
              One showcase for your area. A request goes to the Area Head as employment help, and your phone stays on your profile.
            </p>
          </div>
        </header>

        {!approved ? (
          <p className="help-error" role="alert">
            A talent showcase is available after your membership is approved.
          </p>
        ) : null}
        {error ? (
          <p className="help-error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? <p className="help-notice">{notice}</p> : null}
        {loading ? <p className="help-muted">Loading…</p> : null}

        {approved && !loading ? (
          <form className="help-form" onSubmit={save}>
            <h2 className="help-card-title">{hidden ? 'Hidden showcase' : 'Your showcase'}</h2>
            <label className="help-field">
              Title
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} required />
            </label>
            <label className="help-field">
              What can you do?
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
                required
              />
            </label>
            <fieldset className="help-field">
              <legend>Skills</legend>
              {SKILL_OPTIONS.map((skill) => (
                <label key={skill} className="help-hint">
                  <input type="checkbox" checked={skills.includes(skill)} onChange={() => toggleSkill(skill)} /> {skill}
                </label>
              ))}
            </fieldset>
            <div className="community-photos">
              {photos.map((photo) => (
                <img key={photo} src={assetUrl(photo)} alt="" />
              ))}
            </div>
            {files.length ? <p className="help-hint">{files.length} new photo{files.length === 1 ? '' : 's'} ready to save.</p> : null}
            <label className="help-field">
              Photos, up to three
              <input type="file" accept="image/*" multiple onChange={onPickFiles} disabled={photos.length + files.length >= PHOTO_LIMIT} />
            </label>
            {photos.length ? (
              <button
                type="button"
                className="help-btn"
                onClick={() => setPhotos((current) => current.slice(0, -1))}
              >
                Remove last saved photo
              </button>
            ) : null}
            <button type="submit" className="help-btn primary" disabled={Boolean(busyKey) || skills.length === 0}>
              {busyKey === 'save' ? 'Saving…' : 'Save showcase'}
            </button>
            {title ? (
              <button
                type="button"
                className="help-btn"
                disabled={Boolean(busyKey)}
                onClick={() => setVisibility(!hidden)}
              >
                {hidden ? 'Show in my area' : 'Hide from My Community'}
              </button>
            ) : null}
          </form>
        ) : null}
      </div>
    </div>
  );
}
